import type { DataSource } from "./source";

/**
 * Which parts of the app read the real API, one area at a time.
 *
 * This used to be a single VITE_USE_STUBS flag and it could not do the job
 * that is actually needed. The areas go live at different times because they
 * fill up at different times: a client is something a manager types in, so
 * that table has rows the moment somebody adds one, while productions and
 * roles have nothing in them until the first extraction run lands. Flipping
 * one switch would take every screen live at once and the roles directory
 * would render an empty table, which is worse than the fixtures it replaced.
 *
 * So each area is switched on when it has something to show. A screen reading
 * two areas gets live data for one and fixtures for the other, which is
 * accurate rather than tidy: those really are the states those two things are
 * in.
 */
export type Area =
  | "clients"
  | "matches"
  | "roles"
  | "updates"
  | "calls"
  /**
   * Sending a grid to the backend for extraction. Its own area because it has
   * no fixture worth the name: a grid upload is a write to real storage, so
   * off the real route the stub only acknowledges it. Live once the backend
   * route and its write SAS are in place.
   */
  | "uploads"
  /**
   * The assistant's own endpoint, which is its own area for a reason.
   *
   * It is the only one where the fixtures are not a worse copy of the live
   * thing but a different thing entirely: with it off the panel answers by
   * matching over the productions the browser has loaded, which is what it
   * has always done and what 1v1 has already seen. With it on the model
   * answers. So this switches on when there is a model to switch it on for,
   * and nothing regresses in the meantime.
   */
  | "assistant"
  /**
   * The grid-updates write flow, propose and apply, its own area apart from
   * the assistant for a reason: it does not need the model. The propose and
   * apply routes are real, so this can go live and write to the record while
   * the panel's chat still answers from the productions in the browser. Folded
   * into the assistant area it could not, because that waits on a model and
   * would have kept a finished feature on fixtures for no reason.
   */
  | "grid-updates";

/** Which area each method belongs to, so the split lives in one place rather
 *  than in whichever screen last needed to know. */
export const AREA_OF: Record<keyof DataSource, Area> = {
  listClients: "clients",
  getClient: "clients",
  addClient: "clients",
  updateClient: "clients",
  deleteClient: "clients",

  // Matching a client against the roles that exist. Live only once both the
  // client and the roles it matches against are real, which is why this
  // trails the clients area rather than moving with it.
  listMatchedRoles: "matches",
  listTrackedRoles: "matches",
  listAllTrackedRoles: "matches",
  setTracked: "matches",
  setSeen: "matches",
  markListSeen: "matches",

  chatStatus: "assistant",
  askAssistant: "assistant",
  listAssistantTurns: "assistant",

  // Its own area, so the confirm step can read the real propose and apply
  // routes without waiting on a model for the chat. Off it, the fixtures stand
  // in with a readable proposal, the same way the panel's matcher does.
  proposeProjectChange: "grid-updates",
  applyProjectChange: "grid-updates",

  // A production's people against an address book. With the roles, which
  // is the area the project page reads its production from.
  listKnownContacts: "roles",

  listRoles: "roles",
  listRoleOptions: "roles",
  listGenres: "roles",
  listProjectRoles: "roles",
  getRole: "roles",
  listRoleCandidates: "roles",

  listUpdates: "updates",

  listCalls: "calls",
  addCall: "calls",

  uploadGrid: "uploads",
};

/**
 * VITE_LIVE_AREAS, a comma separated list. "all" is everything, empty is
 * nothing, and an unknown name is ignored rather than throwing: a typo in a
 * deployment variable should cost that one area, not the whole app.
 */
function configured(): Set<Area> {
  const raw = (import.meta.env.VITE_LIVE_AREAS ?? "").trim();
  if (!raw) return new Set();
  if (raw === "all") {
    return new Set(Object.values(AREA_OF));
  }
  const known = new Set<string>(Object.values(AREA_OF));
  return new Set(
    raw
      .split(",")
      .map((name: string) => name.trim())
      .filter((name: string): name is Area => known.has(name)),
  );
}

export const liveAreas: ReadonlySet<Area> = configured();

export function isLive(area: Area): boolean {
  return liveAreas.has(area);
}

/**
 * One source per method, picked by area.
 *
 * Built by walking the contract rather than by listing the methods again, so
 * a method added to DataSource is a type error here until it is given an
 * area. Forgetting one would otherwise mean it quietly stayed on fixtures
 * after everything else went live, which is the kind of thing found weeks
 * later by a manager wondering why one column never updates.
 */
export function compose(live: DataSource, fixtures: DataSource): DataSource {
  const out = {} as Record<string, unknown>;
  for (const name of Object.keys(AREA_OF) as (keyof DataSource)[]) {
    const from = isLive(AREA_OF[name]) ? live : fixtures;
    out[name] = (from[name] as (...args: unknown[]) => unknown).bind(from);
  }
  return out as unknown as DataSource;
}
